import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { createSelector } from 'reselect'

import {
  aggregateAttentionStatuses,
  decodeAttentionStatusMasks,
  getAttentionMasksForOffering,
  parseAttentionStatusMasks,
} from 'partner360/entities/attentionStatuses'

import type { IAttentionPartner } from '../../lib'

import { selectCockpitAttentionStatusCatalog } from './selectCockpitAttentionStatusCatalog'
import { selectCockpitCountsPlansByPartner } from './selectCockpitCountsPlansByPartner'
import { selectCockpitOffering } from './selectCockpitOffering'
import { selectFilteredCockpitPartnerData } from './selectFilteredCockpitPartnerData'

/**
 * The partners the manager cockpit's attention drill-down works on: every partner the cockpit
 * filters currently show, reduced to identity and geo. Changing a filter therefore changes the
 * group the statuses are counted over, which is what the tile's `X of Y` means.
 */
export const selectCockpitAttentionPartners = createSelector(
  [selectFilteredCockpitPartnerData],
  (partnerData): IAttentionPartner[] =>
    partnerData.map((partner) => ({
      partnerId: partner.PartnerId,
      partnerName: partner.PartnerName,
      country: partner.Country,
      countryCode: partner.CountryCode,
    }))
)

/**
 * Statuses raised across the shown partners, ordered by `sortOrder`, with the partners behind each.
 * Each partner's `CockpitAttentionStatus` masks are decoded against the payload's catalog for the
 * selected offering — `Overall` reads every offering's, and a partner with no mask for the
 * offering raises nothing. A row every partner behind it raised for one offering is titled for
 * that offering. The cockpit's filters decide which partners are counted.
 */
const selectCockpitAttentionPartnerMasks = createSelector(
  [selectFilteredCockpitPartnerData, selectCockpitOffering],
  (partnerData, selectedOffering) => {
    const offering = selectedOffering === ProgramTypes.Overall ? undefined : selectedOffering

    return partnerData.map((partner) => ({
      partnerId: partner.PartnerId,
      masks: getAttentionMasksForOffering({
        masks: parseAttentionStatusMasks(partner.CockpitAttentionStatus),
        offering,
      }),
    }))
  }
)

export const selectCockpitMultiPartnerAttentionStatuses = createSelector(
  [
    selectCockpitAttentionStatusCatalog,
    selectCockpitAttentionPartnerMasks,
    selectCockpitCountsPlansByPartner,
  ],
  (catalog, partners, countsPlansByPartner) =>
    aggregateAttentionStatuses({ catalog, partners, countsPlansByPartner })
)

/** One partner raising a status, and the offerings it raised the status for. */
export interface ICockpitAttentionStatusRaise {
  partnerId: string
  offerings: string[]
}

/**
 * Who raised each status across the shown partners, by `bitValue`, with the offerings each partner
 * raised it for. The aggregated statuses keep only the union of offerings; a status' next steps
 * name each partner's own, so `Overall` can list one partner's plans apart.
 */
export const selectCockpitAttentionStatusRaises = createSelector(
  [selectCockpitAttentionStatusCatalog, selectCockpitAttentionPartnerMasks],
  (catalog, partners) => {
    const raises = new Map<number, ICockpitAttentionStatusRaise[]>()

    partners.forEach(({ partnerId, masks }) =>
      decodeAttentionStatusMasks({ masks, catalog }).forEach(({ bitValue, offerings }) =>
        raises.set(bitValue, [...(raises.get(bitValue) ?? []), { partnerId, offerings }])
      )
    )

    return raises
  }
)
